import React, { useEffect, useState } from 'react'
import Loader from 'src/components/Loader'
import Menu from './Menu';
import { MOBILE_VIEW_WIDTH } from 'res/constants';
import { Dimensions } from 'react-native';
import StyleSheetRW from 'core/StyleSheetRW';
import { get, push, ref, remove, update } from 'firebase/database';
import { getLocalStorageItem } from 'core/helpers';
import { formatDate } from 'src/utils/Date&TimeUtils';
import { db } from '@react-native-firebase/database';
import Dialog from 'src/components/Dialog';
import AddProjectForm from './AddProjectForm';
import { BsPen, BsPenFill } from 'react-icons/bs';
import { BiTrash } from 'react-icons/bi';

interface ProjectData {
    id: string;
    projectName: string;
    startDate: string;
    clientName: string;
}

const ProjectScreen: React.FC = () => {
    const screenWidth = Dimensions.get('window').width;
    const loggedinUser = getLocalStorageItem("loggedinUser");
    const [showLoader, setShowLoader] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [projects, setProjects] = useState<ProjectData[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<ProjectData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editProjectId, setEditProjectId] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");

    const getUserTimeRecords = async () => {
        setShowLoader(true);
        try {
            if (loggedinUser) {
                const dbRef = ref(db, `projectRecord/${loggedinUser.uid}`);
                await get(dbRef)
                    .then((snapshot) => {
                        if (snapshot.exists()) {
                            const data = snapshot.val();
                            const records: ProjectData[] = [];
                            Object.keys(data).forEach((uniqueID) => {
                                const { startDate, projectName, clientName } = data[uniqueID];
                                records.push({ startDate, projectName, clientName, id: uniqueID });
                            });
                            setProjects(records);
                            setFilteredProjects(records.reverse());
                            setShowLoader(false);
                        } else {
                            setShowLoader(false);
                            console.log("No data available for the specified user.");
                        }
                    })
                    .catch((error) => {
                        setShowLoader(false);
                        console.error("Failed to retrieve data:", error);
                    });
            }
        } catch (error: any) {
            setShowLoader(false);
            console.error("Error reading data:", error.message);
        }
    };

    const handleAddProject = async (projectData: any) => {
        try {
            if (loggedinUser) {
                const dbRef = ref(db, `projectRecord/${loggedinUser.uid}`);
                await push(dbRef, projectData)
                    .then(() => {
                        setDialogMessage("New project has been added!")
                        setOpenDialog(true)
                        getUserTimeRecords();
                        setOpenForm(false);
                        setShowLoader(false);
                    })
                    .catch((error) => {
                        console.error("Failed to save data:", error);
                    });
            }
        } catch (error) {
            console.error("Error adding document: ", error);
        }
    }

    const handleUpdateProject = async (projectData: any, projectId: string) => {
        try {
            if (loggedinUser) {
                const dbRef = ref(db, `projectRecord/${loggedinUser.uid}/${projectId}`);
                await update(dbRef, projectData)
                    .then(() => {
                        setDialogMessage("Project details has been updated!")
                        setOpenDialog(true)
                        getUserTimeRecords();
                        setOpenForm(false);
                        setShowLoader(false);
                    })
                    .catch((error) => {
                        console.error("Failed to save data:", error);
                    });
            }
        } catch (error) {
            console.error("Error adding document: ", error);
        }
    }

    useEffect(() => {
        getUserTimeRecords();
    }, []);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        setFilteredProjects(
            projects.filter(project =>
                project.projectName.toLowerCase().includes(term)
            )
        );
    };

    const handleDeleteProject = async (id: string) => {
        setShowLoader(true);
        const projectRef = ref(db, `projectRecord/${loggedinUser?.uid}/${id}`);
        await remove(projectRef)
            .then(() => {
                getUserTimeRecords();
                setDialogMessage("Project has been deleted!")
                setOpenDialog(true)
                setShowLoader(false);
            })
            .catch((error) => {
                console.error("Failed to delete project:", error);
                setShowLoader(false);
            });
    }

    const handleEditProjectDetails = (id: string) => {
        setEditProjectId(id);
        setOpenForm(true);
    }

    return (
        <div style={styles.container}>
            <Menu />
            <div style={{ overflowX: "hidden", width: screenWidth < MOBILE_VIEW_WIDTH ? "90%" : "100%", padding: '0px 0px 0px 20px' }}>
                <div style={{ width: screenWidth < MOBILE_VIEW_WIDTH ? "100%" : "97%", fontFamily: "Outfit", }}>
                    <div style={{ width: "100%" }}>
                        <h1 style={styles.heading}>Project</h1>
                        <button
                            style={styles.addButton}
                            onClick={() => setOpenForm(true)}>
                            <span style={styles.addButtonText}>Add New</span>
                        </button>
                        {openForm && (
                            <AddProjectForm
                                onClose={() => { setOpenForm(false); setEditProjectId("") }}
                                onSave={handleAddProject}
                                editProjectId={editProjectId}
                                onUpdate={handleUpdateProject}
                            />
                        )}
                        <div>
                            <div style={styles.inputContainer}>
                                <h2>Projects List</h2>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    placeholder="Search by project name"
                                    style={styles.searchInput}
                                />
                            </div>
                            <div style={{ overflow: "scroll" }}>
                                <table style={styles.tableContainer}>
                                    <thead>
                                        <tr style={styles.tableRow}>
                                            <th style={styles.tableHead}>Project Name</th>
                                            <th style={styles.tableHead}>Start Date</th>
                                            <th style={styles.tableHead}>Client Name</th>
                                            <th style={styles.tableHead}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProjects.map((project, index) => (
                                            <tr key={project.id} style={{ backgroundColor: (index % 2 === 1 ? "#2F48581A" : undefined) }}>
                                                <td style={styles.tableData}>{project.projectName}</td>
                                                <td style={styles.tableData}>{formatDate(project.startDate, "DD-MMM-YYYY")}</td>
                                                <td style={styles.tableLastData}>{project.clientName}</td>
                                                <td style={styles.tableLastData}>
                                                    <button style={styles.actionButton} onClick={() => handleEditProjectDetails(project.id)}>
                                                        <BsPen fontSize={15} />
                                                    </button>
                                                    <button style={{ ...styles.actionButton, marginLeft: 5 }} onClick={() => handleDeleteProject(project.id)}>
                                                        <BiTrash fontSize={15} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {
                            showLoader && <Loader />
                        }
                        <Dialog
                            isDialogVisible={openDialog}
                            title="Project"
                            message={dialogMessage}
                            onDialogClose={() => setOpenDialog(false)}
                            confirmText="okay"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = StyleSheetRW.create(() => ({
    container: { display: 'flex', flexDirection: 'row', height: "100%", fontFamily: "Outfit" },
    heading: { fontWeight: 'bold', fontSize: 32, color: '#2F4858', marginTop: 20, width: "100%" },
    addButton: { width: 147, backgroundColor: '#2F4858', paddingVertical: 10, paddingHorizontal: 20, borderRadius: 100, color: "white", fontWeight: "300", textAlign: "center", borderWidth: 0, alignSelf: "center", height: "38px", cursor: 'pointer' },
    addButtonText: { color: 'white', fontWeight: "400", textAlign: "center" },
    inputContainer: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: "100%", alignItems: "center" },
    searchInput: { height: 40, borderRadius: 10, paddingLeft: 10, paddingRight: 10, backgroundColor: "#0000000D", marginTop: 3, fontFamily: "Outfit", fontSize: 14, fontWeight: "400", color: "#333333", border: 0 },
    tableContainer: { marginBottom: 20, borderCollapse: 'collapse', backgroundColor: '#FFFFFF66', width: '100%' },
    tableHead: { border: '1px solid black', padding: '15px', fontSize: 14, textAlign: "left" },
    tableLastData: { borderLeft: '1px solid #2F485880', padding: '15px', textTransform: "capitalize", maxWidth: "0" },
    tableData: { borderRight: "1px solid #2F485880", padding: '15px', textTransform: "capitalize" },
    tableRow: { backgroundColor: "#2F4858", color: "#FFFFFF" },
    actionButton: { backgroundColor: "#2F4858", color: "white", padding: "5px 15px", borderRadius: 10, border: 0, cursor: "pointer" },
}));

export default ProjectScreen;